import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { API } from "../../../config";
import axios from "axios";

import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";

import { createCerealDictionary } from "../dataTable/DataTableAsyncActions";
import moment from "moment";
import { CerealType } from "../dataTable/DataTableTypes";
import { fetchCropInfo } from "./dynamicCharts";
import {
  setCounOfUserActivityTab,
  setHasDataInCharts,
  setUsersTable,
  startLoading,
  setLoadingProgress,
  resetLoadingProgress,
  resetUsersTable,
  setTotalActions,
} from "../../slices/dashboard/dashboard";

export const transformToUsersTableFormat = (allCompleteData: any[]) => {
  return allCompleteData.reduce(
    (acc, action) => {
      const user = action.username;
      const site = action.geoPointName;
      const cereal = action.cropTextIdLocale || "Unknown";

      // Search for existing entry for this user-site combination
      let userSiteEntry = acc.find(
        (entry) => entry.user === user && entry.site === site,
      );

      if (!userSiteEntry) {
        // Create new entry for this user-site combination
        userSiteEntry = {
          user: user,
          site: site,
          userId: action.userId,
          totalActions: 0,
          varietyActions: [],
        };
        acc.push(userSiteEntry);
      }

      // Increment the total actions count
      userSiteEntry.totalActions += 1;

      // Search for existing variety in varietyActions
      let varietyEntry = userSiteEntry.varietyActions.find(
        (v) => v.cereal === cereal,
      );

      if (!varietyEntry) {
        // Create new entry for this variety
        varietyEntry = {
          cereal: cereal,
          count: 0,
        };
        userSiteEntry.varietyActions.push(varietyEntry);
      }

      // Increment the count for this variety
      varietyEntry.count += 1;

      return acc;
    },
    [] as Array<{
      user: string;
      site: string;
      totalActions: number;
      varietyActions: Array<{
        cereal: string;
        count: number;
      }>;
    }>,
  );
};

export const transformToChartActivityFormat = (
  allCompleteData: any[],
  dataDateActivity: Date[],
  locale: string,
  cerealDictionary: Record<number, CerealType>,
) => {
  // Early return if no data
  if (!allCompleteData || allCompleteData.length === 0) {
    return { data: [] };
  }

  const startDate = new Date(dataDateActivity[0]);
  const endDate = new Date(dataDateActivity[1]);

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  // Pre-calculate all date strings and varieties in a single pass
  const dateToActionsMap = new Map<string, Map<string, number>>();
  const varietiesSet = new Set<string>();
  const varietyColorMap = new Map<string, string>();

  // Helper function
  const getDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Single pass through data to build maps (O(n) instead of O(n*m))
  for (const action of allCompleteData) {
    const variety = action.cropTextIdLocale || "Unknown";
    const dateString = getDateString(new Date(action.createdAt));

    // Track variety
    varietiesSet.add(variety);

    // Get color for variety (cache it)
    if (!varietyColorMap.has(variety) && action.cropTextId?.id) {
      const varietyEntry = cerealDictionary[action.cropTextId.id];
      varietyColorMap.set(
        variety,
        varietyEntry?.iconBackgroundHexColor || "#cccccc",
      );
    }

    // Initialize date map if needed
    if (!dateToActionsMap.has(dateString)) {
      dateToActionsMap.set(dateString, new Map<string, number>());
    }

    // Increment count for this date-variety combination
    const dateMap = dateToActionsMap.get(dateString)!;
    dateMap.set(variety, (dateMap.get(variety) || 0) + 1);
  }

  // Set default color for varieties without color
  for (const variety of varietiesSet) {
    if (!varietyColorMap.has(variety)) {
      varietyColorMap.set(variety, "#cccccc");
    }
  }

  // Initialize varieties data structure
  const varietiesData: Record<
    string,
    { date: string; count: number; color: string }[]
  > = {};

  for (const variety of varietiesSet) {
    varietiesData[variety] = [];
  }

  // Process each day in the date range
  const currentDate = new Date(startDate);
  const endDateString = getDateString(endDate);

  while (getDateString(currentDate) <= endDateString) {
    const dateString = getDateString(currentDate);
    const dateMap = dateToActionsMap.get(dateString);

    // Add daily counts to varietiesData
    for (const variety of varietiesSet) {
      const count = dateMap?.get(variety) || 0;
      varietiesData[variety].push({
        date: dateString,
        count: count,
        color: varietyColorMap.get(variety)!,
      });
    }

    // Increment date by one day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Construct the final cereals array
  const cerealsArray = Object.entries(varietiesData).map(
    ([specie, countInfo]) => ({
      specie: specie,
      color: varietyColorMap.get(specie)!,
      countInfo: countInfo.map((values) => {
        const momentDate = moment(values.date + "T00:00:00").locale(locale);
        const formattedDate = momentDate.format("l");

        return {
          date: formattedDate,
          activity: values.count,
        };
      }),
    }),
  );

  return { data: cerealsArray };
};

export const fetchUsers = async (
  token: string,
  locale: string,
): Promise<any> => {
  try {
    const result = await axios.get(`${API}/users/user`, {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    });
    const users = result.data.data.map(
      (user: { id: string; username: string }) => ({
        id: user.id,
        username: user.username,
      }),
    );

    return users;
  } catch (error) {
    return error;
  }
};

export const activityChartValues = createAsyncThunk(
  "SharedDataReducer/activityChartValues",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { cerealInformation } = state.DataTableReducer;
    const { dataDateActivity, locations } = state.DashboardFiltersReducer;
    const { selectedCrop } = state.DashboardReducer;

    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0]),
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHoursSameTime(
      setZeroHrs(dataDateActivity[1]),
    ).toISOString()}`;

    const filterLocation = locations.valuesSelected
      ?.flatMap((location) => `&geoPointName[$in][]=${location.name}`)
      .join("");

    // Increase initial limit for fewer requests
    const batchSize = 1000; // Increased from 100

    const queryParams = [`$limit=${batchSize}`, fromDate, toDate]
      .filter(Boolean)
      .join("&");

    try {
      store.dispatch(resetUsersTable());
      store.dispatch(setTotalActions(0));
      store.dispatch(startLoading(0));

      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}${
          locations.allValuesSelected === true ? "" : filterLocation
        }`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );

      if (selectedCrop.slug.length > 0) {
        store.dispatch(fetchCropInfo(selectedCrop.slug));
      }

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store,
      );

      const currentPageData = result.data.data;
      const limit = result.data.limit;
      const total = result.data.total; // Use actual total from backend

      // Set total actions in independent state (won't be reset)
      store.dispatch(setTotalActions(total));

      store.dispatch(
        setHasDataInCharts({ hasData: total > 0, hasDataBeenLoaded: true }),
      );

      let allCompleteData = [...currentPageData];
      let current = limit;

      // Update progress with actual total and first batch loaded
      store.dispatch(
        setLoadingProgress({ loaded: allCompleteData.length, total }),
      );

      // Batch requests with Promise.all for parallel processing (max 5 concurrent)
      const fetchBatch = async (skip: number) => {
        return axios.get(
          `${API}/portal/managed-data-table?userFacingDeletion=false&$skip=${skip}&$limit=${batchSize}&${fromDate}&${toDate}${
            locations.allValuesSelected === true ? "" : filterLocation
          }`,
          {
            headers: {
              "Accept-Language": locale,
              Authorization: `Bearer ${user!.token}`,
            },
          },
        );
      };

      const batchPromises: Promise<any>[] = [];
      const concurrentLimit = 5; // Process 5 requests at a time

      while (current < total) {
        batchPromises.push(fetchBatch(current));
        current += batchSize;

        // Process in batches to avoid overwhelming the server
        if (batchPromises.length >= concurrentLimit || current >= total) {
          const results = await Promise.all(batchPromises);
          results.forEach((subResult) => {
            allCompleteData.push(...subResult.data.data);
          });
          batchPromises.length = 0; // Clear array

          // Update loading progress
          store.dispatch(
            setLoadingProgress({ loaded: allCompleteData.length, total }),
          );
        }
      }

      const usersTable = transformToUsersTableFormat(allCompleteData);

      const users = await fetchUsers(user!.token, locale);
      const activeUsers = usersTable.filter(
        (user) => user.totalActions > 0,
      ).length;

      const myCountUserObject = {
        totalUsers: users.length,
        countOfUsersActive: activeUsers,
        countOfUsersInactive: users.length - activeUsers,
      };

      store.dispatch(setUsersTable(usersTable));
      store.dispatch(setCounOfUserActivityTab(myCountUserObject));

      const transformedData = transformToChartActivityFormat(
        allCompleteData,
        dataDateActivity,
        locale,
        cerealDictionary,
      );

      // Reset loading progress after a delay (non-blocking for data return)
      setTimeout(() => {
        store.dispatch(resetLoadingProgress());
      }, 1000);

      return transformedData;
    } catch (error) {
      // Reset loading progress on error
      store.dispatch(resetLoadingProgress());
      return store.rejectWithValue(error);
    }
  },
);

export const fetchCerealTypeDataTableDictionary = createAsyncThunk(
  "SharedDataReducer/fetchCerealTypeDataTable",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    try {
      const response = await axios.get(`${API}/core/cereal-type`, {
        headers: {
          "Accept-Language": locale,
          Authorization: `Bearer ${user!.token}`,
        },
      });

      return response.data.data as CerealType[];
    } catch (error: any) {
      return error;
    }
  },
);

export const fetchUserActivity = createAsyncThunk(
  "DashboardReducer/actifetchUserActivityvity",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { dataDateActivity, locations } = state.DashboardFiltersReducer;

    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0]),
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHoursSameTime(
      setZeroHrs(dataDateActivity[1]),
    ).toISOString()}`;

    const filterLocation = locations.valuesSelected
      ?.flatMap((location) => `&geoPointName[$in][]=${location.name}`)
      .join("");

    try {
      const res = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${fromDate}&${toDate}&dailyCount=1${
          locations.allValuesSelected === true ? "" : filterLocation
        }`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );

      const rawData = res.data.data;

      const cerealTypesResponse = await store.dispatch(
        fetchCerealTypeDataTableDictionary(),
      );
      const cerealTypes = cerealTypesResponse.payload as CerealType[];

      const cerealDictionary = cerealTypes.reduce(
        (acc: Record<number, CerealType>, cereal: CerealType) => {
          acc[cereal.id] = cereal;
          return acc;
        },
        {},
      );

      const startDate = new Date(dataDateActivity[0]);
      const endDate = new Date(dataDateActivity[1]);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const getDateString = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const allDates: string[] = [];
      const currentDate = new Date(startDate);
      const endDateString = getDateString(endDate);

      while (getDateString(currentDate) <= endDateString) {
        allDates.push(getDateString(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const groupedByCereal = rawData.reduce((acc: any, item: any) => {
        const cerealTypeId = item.cerealTypeId;
        const itemDate = getDateString(new Date(item.createdDay));
        const count = Number.parseInt(item.count, 10);

        if (!acc[cerealTypeId]) {
          acc[cerealTypeId] = {};
        }
        if (acc[cerealTypeId][itemDate]) {
          acc[cerealTypeId][itemDate].activity += count;
        } else {
          acc[cerealTypeId][itemDate] = {
            activity: count,
            date: moment(item.createdDay).locale(locale).format("l"),
          };
        }

        return acc;
      }, {});

      const result = Object.entries(groupedByCereal).map(
        ([cerealTypeId, dateData]: [string, any]) => {
          const cerealType =
            cerealDictionary[Number.parseInt(cerealTypeId, 10)];

          const completeData = allDates.map((dateString) => {
            if (dateData[dateString]) {
              return dateData[dateString];
            } else {
              return {
                activity: 0,
                date: moment(dateString).locale(locale).format("l"),
              };
            }
          });

          return {
            specie: cerealType?.textId.text,
            countInfo: completeData,
            color: cerealType?.iconBackgroundHexColor || "#cccccc",
          };
        },
      );

      return result;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);
